import React, { useState } from 'react';
import { Wallet } from 'lucide-react';
import { useApp } from '../context/AppContext';

const LoginScreen = () => {
  const { login, loading, error, setError } = useApp();
  const [tokenInput, setTokenInput] = useState('');
  const [useToken, setUseToken] = useState(true);
  const [credentials, setCredentials] = useState({ email: '', password: '' });

  const handleTokenLogin = async () => {
    if (!tokenInput.trim()) {
      setError('Please enter your API token');
      return;
    }
    try {
      await login(tokenInput.trim());
    } catch (err) {
      // Error handled in context
    }
  };

  const handleEmailLogin = async () => {
    if (!credentials.email || !credentials.password) {
      setError('Please enter email and password');
      return;
    }

    try {
      // Note: Browser login may not work due to CORS
      const formData = new URLSearchParams();
      formData.append('email', credentials.email);
      formData.append('password', credentials.password);

      const response = await fetch('https://www.buxfer.com/api/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
        mode: 'cors',
        credentials: 'omit'
      });

      const data = await response.json();

      if (data.response && data.response.status === 'OK' && data.response.token) {
        await login(data.response.token);
      } else {
        setError(data.response?.status || 'Login failed. Please check your credentials.');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(`CORS Error: Buxfer API doesn't allow browser access. You need to use a token directly.`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <Wallet className="w-16 h-16 mx-auto text-indigo-600 mb-4" />
          <h1 className="text-2xl font-bold text-gray-800">Buxfer Tracker</h1>
          <p className="text-gray-600 mt-2">Sign in to get started</p>
        </div>

        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setUseToken(true)}
            className={`flex-1 py-2 rounded-lg font-medium transition ${
              useToken ? 'bg-indigo-100 text-indigo-700' : 'text-gray-600'
            }`}
          >
            Use Token
          </button>
          <button
            onClick={() => setUseToken(false)}
            className={`flex-1 py-2 rounded-lg font-medium transition ${
              !useToken ? 'bg-indigo-100 text-indigo-700' : 'text-gray-600'
            }`}
          >
            Email/Password
          </button>
        </div>

        {useToken ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">API Token</label>
              <input
                type="password"
                placeholder="Enter your Buxfer API token"
                value={tokenInput}
                onChange={(e) => setTokenInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleTokenLogin()}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            {error && (
              <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <button
              onClick={handleTokenLogin}
              disabled={loading}
              className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition disabled:bg-gray-400"
            >
              {loading ? 'Connecting...' : 'Connect'}
            </button>

            <div className="text-xs text-gray-500 space-y-1">
              <p>Get your token by logging in via email/password first, or from:</p>
              <p className="font-mono bg-gray-100 p-2 rounded">curl -d "email=YOUR_EMAIL&password=YOUR_PASSWORD" https://www.buxfer.com/api/login</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                placeholder="your@email.com"
                value={credentials.email}
                onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <input
                type="password"
                placeholder="********"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                onKeyPress={(e) => e.key === 'Enter' && handleEmailLogin()}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            {error && (
              <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <button
              onClick={handleEmailLogin}
              disabled={loading}
              className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition disabled:bg-gray-400"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>

            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg text-xs">
              <p className="font-semibold mb-1">Note:</p>
              <p>Browser login may not work due to CORS. Use "Use Token" tab and get your token from terminal or Postman.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginScreen;
